import * as Enumerable from "linq";
import { MepEmissionsTargetColourEnum } from "../../../../../../../../constants/mep/mep.constants";
import { MepDashboardTotalEmissions, MepEmissionColourDetails, MepDashboardSummaryResponse, MepDashboardRequest, GetMepEmissionsAdditionalDetailsRequest, MepDashboardTotalEmissionsByScope } from "../../../../../../../../models/mep/mep.models";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import { BaseMepSummaryRequest, MepPortSummaryEmissionRequest, MepPortSummaryTotalEmissionsResponse } from "../../../../../../../../models/mep/mep-summary.model";
import { useView } from "aurelia-framework";
import { MepSummaryService } from "../../../../../../../../services/mep/mep-summary.service";
import { MepDashboardFilter } from "../mep-dashboard-filter/mep-dashboard-filter";
import { MepPortsSummaryFilter } from "../../../mep-home/partials/mep-ports-summary-filter/mep-ports-summary-filter";

@useView('views/pages/modules/mep/tabs/mep-dashboard/partials/mep-single-emission-type/mep-single-emission-type.html')
export class MepSingleEmissionType {

    loadingAdditionalSingleEmissionData: boolean = false;
    emissionsPerPortActivityPerHour: number;
    selectedEmission: MepDashboardTotalEmissions;
    selectedemissionByScope: MepDashboardTotalEmissionsByScope;
    shouldShowSingleEmission: boolean;
    speedometerViewModel: SpeedometerViewModel;
    colourDetails: MepEmissionColourDetails[];
    maxLowEmission: number;
    maxMediumEmission: number;

    constructor(private service: MepService | MepSummaryService, private filtersViewModel: MepDashboardFilter | MepPortsSummaryFilter) {
    }

    public refreshData(summaryPromise: Promise<MepDashboardSummaryResponse>, dashboardRequest: MepDashboardRequest) {

        // we activate this view model if only 1 emission type is selected 
        this.shouldShowSingleEmission = this.filtersViewModel.selectedEmissionTypes && this.filtersViewModel.selectedEmissionTypes.length == 1;
        this.selectedEmission = null;
        this.speedometerViewModel = null;

        if (!this.shouldShowSingleEmission) // do nothing in such a case 
        {
            return null;
        }

        this.loadingAdditionalSingleEmissionData = true;

        return new Promise<string>((resolve, reject) => {
            const mepService = this.service as MepService;

            const request: GetMepEmissionsAdditionalDetailsRequest = {
                portId: dashboardRequest.portId,
                vesselTypeIds: dashboardRequest.vesselTypeIds,
                vesselTypeGroupIds: dashboardRequest.vesselTypeGroupIds,
                emissionTypeId: dashboardRequest.emissionTypeIds[0], // since we have this.filtersViewModel.selectedEmissionTypes i.e. 1 only is selected 
                pointOfInterestIds: dashboardRequest.pointOfInterestIds,
                operationModeIds: dashboardRequest.operationModeIds,
                cargoTypeIds: dashboardRequest.cargoTypeIds,
                year: dashboardRequest.year,
                months: dashboardRequest.months
            };

            mepService.getMepEmissionsAdditionalDetailsForPortDashboard(request).then(response => {
                if (!response.hasError) {
                    //then we will use the dashboard promise to get the emission details so that the bindings are all with this view model
                    summaryPromise.then(mepSummaryResponse => {

                        if (!mepSummaryResponse.hasError && mepSummaryResponse.totalEmissionsPerType && mepSummaryResponse.totalEmissionsPerType.length > 0) {
                            this.selectedEmission = mepSummaryResponse.totalEmissionsPerType[0];
                            this.selectedemissionByScope = mepSummaryResponse.totalEmissionsByScope[0];
                            if (response.mepEmissionsPerPortActivityPerHour) {
                                this.emissionsPerPortActivityPerHour = response.mepEmissionsPerPortActivityPerHour.emissionValueInTonnes;
                            }
                            this.generateChartData(response);
                        }
                    });
                }

                this.loadingAdditionalSingleEmissionData = false;
                resolve(null);
            }).catch(() => {
                this.loadingAdditionalSingleEmissionData = false;
                reject("MEP Emissions Per Port Activity Per Hour - Error retrieving the emissions per port activity per hour!");
            });
        });
    }

    public refreshPortSummaryData(summaryPromise: Promise<MepPortSummaryTotalEmissionsResponse>, summaryRequest: BaseMepSummaryRequest) {
        this.shouldShowSingleEmission = this.filtersViewModel.selectedEmissionTypes && this.filtersViewModel.selectedEmissionTypes.length == 1;
        this.selectedEmission = null;
        this.speedometerViewModel = null;

        if (!this.shouldShowSingleEmission) // do nothing in such a case 
        {
            return null;
        }

        this.loadingAdditionalSingleEmissionData = true;

        return new Promise<string>((resolve, reject) => {

            const mepSummaryService = this.service as MepSummaryService;

            const request: MepPortSummaryEmissionRequest = {
                portIds: summaryRequest.portIds,
                vesselTypeGroupIds: summaryRequest.vesselTypeGroupIds,
                emissionTypeId: summaryRequest.emissionTypeIds[0],
                pointOfInterestIds: summaryRequest.pointOfInterestIds,
                operationModeIds: summaryRequest.operationModeIds,
                cargoTypeIds: summaryRequest.cargoTypeIds,
                year: summaryRequest.year,
                monthNumbers: summaryRequest.monthNumbers,
                scopeIds: summaryRequest.scopeIds
            };

            mepSummaryService.getMepPortSummaryEmission(request).then(response => {
                if (!response.hasError) {
                    summaryPromise.then(mepSummaryResponse => {
                        if (!mepSummaryResponse.hasError && mepSummaryResponse.totalEmissionsByTypes && mepSummaryResponse.totalEmissionsByTypes.length > 0) {
                            this.selectedEmission = mepSummaryResponse.totalEmissionsByTypes[0];
                            this.selectedemissionByScope = mepSummaryResponse.totalEmissionsByScopes[0];
                            this.emissionsPerPortActivityPerHour = response.emissionValueInTonnes;
                            this.generateChartData(response);
                        }
                    });
                }

                this.loadingAdditionalSingleEmissionData = false;
                resolve(null);
            }).catch(() => {
                this.loadingAdditionalSingleEmissionData = false;
                reject("MEP Emissions Per Port Activity Per Hour - Error retrieving the emissions per port activity per hour!");
            });
        });
    }

    public generateChartData(response: any) {
        if (response.colourDetails && response.colourDetails.length >= 3) //since we need at least 3 bands low,medium,high, any other band we will ignore 
        {
            this.colourDetails = response.colourDetails;

            const colourDetailsQuery = Enumerable.from(this.colourDetails);
            const low = colourDetailsQuery.firstOrDefault(x => x.colourCode == MepEmissionsTargetColourEnum.Low);
            const medium = colourDetailsQuery.firstOrDefault(x => x.colourCode == MepEmissionsTargetColourEnum.Medium);
            const high = colourDetailsQuery.firstOrDefault(x => x.colourCode == MepEmissionsTargetColourEnum.High);

            if (low && medium && high) {
                this.maxLowEmission = low.upperValue;

                this.maxMediumEmission = medium.upperValue;

                //lets calculate percentages for speedo 
                const lowBand = low.upperValue - low.lowerValue;
                const mediumBand = medium.upperValue - medium.lowerValue;
                const highBand = Math.abs((high.upperValue ?? 0) - high.lowerValue);
                const totalBands = lowBand + mediumBand + highBand;

                if (totalBands == 0) {
                    this.colourDetails = [];
                    this.speedometerViewModel = new SpeedometerViewModel(0, 0, 0, 0);
                    return;
                }

                const lowPercentage = lowBand / totalBands * 100;
                const mediumPercentage = mediumBand / totalBands * 100;
                const highPercentage = highBand / totalBands * 100;

                //calculate percentage of value vs high value
                let currentPositionPercentage = (this.emissionsPerPortActivityPerHour / highBand) * 100;
                //calculate actual percentage vs high percentage
                currentPositionPercentage = (currentPositionPercentage * highPercentage) / 100;
                currentPositionPercentage = Math.min(currentPositionPercentage, 100); //the percentages shouldn't be greater then 100% as it'll cause pointer exceed the graph
                this.speedometerViewModel = new SpeedometerViewModel(lowPercentage, mediumPercentage, highPercentage, currentPositionPercentage);
            }
        } else {
            this.maxLowEmission = 0;
            this.maxMediumEmission = 0;
            //if we do not have color settings we should show an empty one 
            this.speedometerViewModel = new SpeedometerViewModel(0, 0, 0, 0);
        }
    }
}

export class SpeedometerViewModel {

    public circleDashoffset: number = 880; //width of speedometer circle
    public graphDashoffset: number = 370; //width of speedometer graph
    public rotationStartPoint: number = 165.6; //speedometer rotation start point
    public speedometerAmount: number = this.speedometerPointer(90); //speedometer pointer emission amount
    public shouldShowUnSet: boolean = false;
    public percentageValueOfMedium: number;

    public speedometerBarLow: number = 0;
    public speedometerBarMedium: number = 0;
    public speedometerBarHigh: number = 0;
    public speedometerRotateLow: number = 0;
    public speedometerRotateMedium: number = 0;
    public speedometerRotateHigh: number = 0;
    public labelRotateLow: number = 0;
    public labelRotateMedium: number = 0;
    public labelRotateHigh: number = 0;

    constructor(low: number, medium: number, high: number, currentValue: number) {

        this.shouldShowUnSet = currentValue == 0;
        this.speedometerAmount = this.speedometerPointer(currentValue); //speedometer pointer emission amount

        this.speedometerBarLow = this.speedometerBarLength(low);
        this.speedometerBarMedium = this.speedometerBarLength(medium);
        this.speedometerBarHigh = this.speedometerBarLength(high);

        this.speedometerRotateLow = this.rotateBarGraph(0);
        this.speedometerRotateMedium = this.rotateBarGraph(low);
        this.speedometerRotateHigh = this.rotateBarGraph(low + medium);

        this.labelRotateLow = this.rotateLabel(0);
        this.labelRotateMedium = this.rotateLabel(low);
        this.labelRotateHigh = this.rotateLabel(low + medium);

        this.percentageValueOfMedium = medium;
    }

    speedometerBarLength(percentage: number) {
        const percentOfgraph: number = 100 - ((this.graphDashoffset * 100) / this.circleDashoffset);
        const percentageOfBar: number = (percentage / 100) * percentOfgraph;
        const convertToPercent: number = (this.circleDashoffset - (percentageOfBar / 100) * this.circleDashoffset);
        return convertToPercent;
    }

    rotateBarGraph(percentage: number) {
        const percentOfgraph: number = 100 - ((this.graphDashoffset * 100) / this.circleDashoffset);
        const percentageOfbar: number = (percentage * percentOfgraph) / 100;
        const rotationOfBar: number = (percentageOfbar / 100) * 360;
        const degreeToAdd: number = this.rotationStartPoint + rotationOfBar;
        return degreeToAdd;
    }

    speedometerPointer(amount: number) {
        const PointerStartPoint: number = 300;
        const percentOfgraph: number = 100 - ((this.graphDashoffset * 100) / this.circleDashoffset);
        const percentageOfbar: number = (amount * percentOfgraph) / 100;
        const rotationOfBar: number = (percentageOfbar / 100) * 360;
        const degreeToAdd: number = PointerStartPoint + rotationOfBar;
        return degreeToAdd;
    }

    rotateLabel(amount: number) {
        const labelStartPoint: number = 300;
        const percentOfgraph: number = 100 - ((this.graphDashoffset * 100) / (this.circleDashoffset == 0 ? 1 : this.circleDashoffset));
        const percentageOfbar: number = (amount * percentOfgraph) / 100;
        const rotationOfBar: number = (percentageOfbar / 100) * 360;
        const degreeToAdd: number = labelStartPoint + rotationOfBar;
        return degreeToAdd;
    }
}